import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

export default function MetaAdLibraryLab() {
  const [searchTerms, setSearchTerms] = useState('');
  const [adType, setAdType] = useState('ALL'); // ALL | EMPLOYMENT_ADS | FINANCIAL_PRODUCTS_AND_SERVICES_ADS | HOUSING_ADS | POLITICAL_AND_ISSUE_ADS
  const [countries, setCountries] = useState("['TR']"); // JSON array string or comma-separated
  const [status, setStatus] = useState('ALL'); // forced to ALL
  const [dateMin, setDateMin] = useState('');
  const [dateMax, setDateMax] = useState('');
  const [publisherPlatforms, setPublisherPlatforms] = useState('FACEBOOK,INSTAGRAM');
  const [mediaType, setMediaType] = useState('ALL'); // ALL | IMAGE | MEME | VIDEO | NONE
  const [pageIds, setPageIds] = useState('');
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [after, setAfter] = useState('');
  const [pagingNext, setPagingNext] = useState('');
  const [previews, setPreviews] = useState({}); // id -> { loading, mediaUrl, mediaType, error }
  const [advQuery, setAdvQuery] = useState('');
  const [advLoading, setAdvLoading] = useState(false);
  const [advResults, setAdvResults] = useState([]);
  const [uname, setUname] = useState('');
  const [unameLoading, setUnameLoading] = useState(false);

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const baseHeaders = { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) };

  const makeArrayParam = (raw) => {
    if (!raw) return undefined;
    if (Array.isArray(raw)) return JSON.stringify(raw);
    const s = String(raw).trim();
    if (!s) return undefined;
    if (s.startsWith('[') && s.endsWith(']')) return s;
    const parts = s.split(',').map(v => v.trim()).filter(Boolean);
    return JSON.stringify(parts);
  };

  const buildQuery = useCallback((cursorAfter, overridePageIds, overrideCountries) => {
    const params = new URLSearchParams();
    // Ensure JSON array encoding for countries
    const c = makeArrayParam(overrideCountries != null ? overrideCountries : countries);
    if (c) params.set('ad_reached_countries', c);
    // Force ALL types and statuses
    params.set('ad_type', 'ALL');
    params.set('ad_active_status', 'ALL');
    // Force last 90 days window by default
    const today = new Date();
    const ymd = (d) => d.toISOString().slice(0, 10);
    const since = new Date(today);
    since.setUTCDate(since.getUTCDate() - 89);
    params.set('ad_delivery_date_min', ymd(since));
    params.set('ad_delivery_date_max', ymd(today));
    const pubs = makeArrayParam(publisherPlatforms);
    if (pubs) params.set('publisher_platforms', pubs);
    if (mediaType) params.set('media_type', mediaType);
    const pages = makeArrayParam(overridePageIds != null ? overridePageIds : pageIds);
    if (pages) params.set('search_page_ids', pages);
    const n = Math.max(1, Math.min(1000, parseInt(limit, 10) || 50));
    params.set('limit', String(n));
    if (cursorAfter) params.set('after', cursorAfter);
    return params.toString();
  }, [countries, publisherPlatforms, mediaType, pageIds, limit]);

  const runSearch = useCallback(async () => {
    try {
      // Force TR only
      const forcedCountries = "['TR']";
      if (!(uname || '').trim() && !(pageIds || '').trim()) {
        toast.error('Enter an Instagram username or pick a Page from the table');
        return;
      }
      setLoading(true);
      setRows([]);
      setAfter('');
      setPagingNext('');
      // Resolve username first (if provided)
      if ((uname || '').trim()) {
        try {
          const params = new URLSearchParams();
          params.set('username', (uname || '').trim());
          const r = await fetch(`https://api.glowytics.com/api/meta-ads/library/advertisers/resolve-username?${params.toString()}`, {
            method: 'GET',
            credentials: 'include',
            headers: baseHeaders
          });
          const data = await r.json().catch(() => ({}));
          if (r.ok && data?.ok && data?.page?.page_id) {
            setPageIds(String(data.page.page_id));
          } else {
            throw new Error(data?.error || 'Username not found');
          }
        } catch (e) {
          throw new Error(e.message || 'Username resolve failed');
        }
      }
      const qs = buildQuery('', undefined, forcedCountries);
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/library/search?${qs}`, { credentials: 'include', headers: baseHeaders, method: 'GET' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Meta Ad Library request failed');
      }
      const list = Array.isArray(data?.data) ? data.data : [];
      setRows(list);
      const next = data?.paging?.next || '';
      const cursorAfter = data?.paging?.cursors?.after || '';
      setPagingNext(next || '');
      setAfter(cursorAfter || '');
      toast.success(`Fetched ${list.length} ads`);
    } catch (e) {
      console.error('[MetaAdLibraryLab] error', e);
      toast.error(e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [countries, buildQuery, baseHeaders]);

  const loadMore = useCallback(async () => {
    try {
      if (!after) return;
      setLoading(true);
      const qs = buildQuery(after);
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/library/search?${qs}`, { credentials: 'include', headers: baseHeaders, method: 'GET' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Meta Ad Library request failed');
      }
      const list = Array.isArray(data?.data) ? data.data : [];
      setRows(prev => [...prev, ...list]);
      const next = data?.paging?.next || '';
      const cursorAfter = data?.paging?.cursors?.after || '';
      setPagingNext(next || '');
      setAfter(cursorAfter || '');
      toast.success(`Loaded ${list.length} more`);
    } catch (e) {
      console.error('[MetaAdLibraryLab] loadMore error', e);
      toast.error(e.message || 'Load more failed');
    } finally {
      setLoading(false);
    }
  }, [after, buildQuery, baseHeaders]);

  // Trigger search immediately for a selected page id
  const searchForPageId = useCallback(async (pid) => {
    try {
      if (!pid) return;
      setLoading(true);
      setRows([]);
      setAfter('');
      setPagingNext('');
      setPageIds(String(pid));
      // Force TR only
      const qs = buildQuery('', String(pid), "['TR']");
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/library/search?${qs}`, { credentials: 'include', headers: baseHeaders, method: 'GET' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Meta Ad Library request failed');
      const list = Array.isArray(data?.data) ? data.data : [];
      setRows(list);
      const next = data?.paging?.next || '';
      const cursorAfter = data?.paging?.cursors?.after || '';
      setPagingNext(next || '');
      setAfter(cursorAfter || '');
      toast.success(`Fetched ${list.length} ads`);
    } catch (e) {
      toast.error(e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [baseHeaders, buildQuery]);

  // Auto-prefetch inline previews (first 20) with small concurrency
  React.useEffect(() => {
    let aborted = false;
    const run = async () => {
      const max = 20;
      const conc = 4;
      const targets = rows.slice(0, max).filter(r => r?.id && r?.ad_snapshot_url);
      const queue = targets.filter(r => !previews[r.id]?.mediaUrl && !previews[r.id]?.loading && !previews[r.id]?.error);
      const work = async (r) => {
        if (aborted) return;
        try {
          setPreviews(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), loading: true, error: '' } }));
          const res = await fetch(`https://api.glowytics.com/api/meta-ads/library/preview?url=${encodeURIComponent(r.ad_snapshot_url)}`, {
            credentials: 'include',
            headers: baseHeaders,
            method: 'GET'
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data?.ok) throw new Error(data.error || 'Preview fetch failed');
          if (aborted) return;
          setPreviews(prev => ({ ...prev, [r.id]: { loading: false, mediaUrl: data.mediaUrl || '', mediaType: data.mediaType || 'unknown', error: '' } }));
        } catch (e) {
          if (aborted) return;
          setPreviews(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), loading: false, error: e.message || 'Preview failed' } }));
        }
      };
      const batches = [];
      for (let i = 0; i < queue.length; i += conc) {
        batches.push(queue.slice(i, i + conc));
      }
      for (const b of batches) {
        await Promise.all(b.map(work));
        if (aborted) break;
      }
    };
    if (Array.isArray(rows) && rows.length) run();
    return () => { aborted = true; };
  }, [rows, previews, baseHeaders]);

  const findAdvertisers = useCallback(async () => {
    try {
      if (!advQuery.trim()) return;
      setAdvLoading(true);
      setAdvResults([]);
      const params = new URLSearchParams();
      params.set('query', advQuery.trim());
      const c = makeArrayParam(countries);
      if (c) params.set('ad_reached_countries', c);
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/library/advertisers/find-by-keyword?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: baseHeaders
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Suggest failed');
      const list = Array.isArray(data.advertisers) ? data.advertisers : [];
      const q = (advQuery || '').trim().toLowerCase();
      const filtered = q ? list.filter(a => String(a.page_name || '').toLowerCase().includes(q)) : list;
      setAdvResults(filtered);
      if (filtered.length === 0) {
        toast('No pages found for this query');
      } else {
        toast.success(`Found ${filtered.length} page(s)`);
      }
    } catch (e) {
      console.error('[MetaAdLibraryLab] suggest error', e);
      setAdvResults([]);
      toast.error(e.message || 'Suggest failed');
    } finally {
      setAdvLoading(false);
    }
  }, [advQuery, baseHeaders, countries]);

  const addAdvertiser = useCallback((pid) => {
    const current = (pageIds || '').trim();
    const list = current ? current.split(',').map(s => s.trim()).filter(Boolean) : [];
    if (!list.includes(pid)) {
      const updated = [...list, pid].join(',');
      setPageIds(updated);
    }
  }, [pageIds]);

  const resolveUsername = useCallback(async () => {
    try {
      const u = (uname || '').trim();
      if (!u) return;
      setUnameLoading(true);
      const params = new URLSearchParams();
      params.set('username', u);
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/library/advertisers/resolve-username?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: baseHeaders
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok || !data?.page?.page_id) {
        throw new Error(data.error || 'Username not found');
      }
      addAdvertiser(String(data.page.page_id));
    } catch (e) {
      // no toast to keep UI minimal
      console.error('resolve username error', e);
    } finally {
      setUnameLoading(false);
    }
  }, [uname, baseHeaders, addAdvertiser]);

  const renderRow = (r, idx) => {
    const snapshotUrl = r?.ad_snapshot_url || '';
    const pageName = r?.page_name || '';
    const pageId = r?.page_id || '';
    const start = r?.ad_delivery_start_time || '';
    const stop = r?.ad_delivery_stop_time || '';
    const platforms = Array.isArray(r?.publisher_platforms) ? r.publisher_platforms.join(', ') : (r?.publisher_platforms || '');
    const id = r?.id || idx;
    const pv = previews[id] || {};
    const triggerPreview = async () => {
      if (!snapshotUrl) return;
      if (pv.mediaUrl || pv.loading) return;
      try {
        setPreviews(prev => ({ ...prev, [id]: { ...(prev[id] || {}), loading: true, error: '' } }));
        const res = await fetch(`https://api.glowytics.com/api/meta-ads/library/preview?url=${encodeURIComponent(snapshotUrl)}`, {
          credentials: 'include',
          headers: baseHeaders,
          method: 'GET'
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) {
          throw new Error(data.error || 'Preview fetch failed');
        }
        setPreviews(prev => ({ ...prev, [id]: { loading: false, mediaUrl: data.mediaUrl || '', mediaType: data.mediaType || 'unknown', error: '' } }));
      } catch (e) {
        setPreviews(prev => ({ ...prev, [id]: { ...(prev[id] || {}), loading: false, error: e.message || 'Preview failed' } }));
      }
    };
    return (
      <div key={`${id}-${idx}`} className="border rounded p-3 mb-3 bg-white shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-900">{pageName || '(no page name)'}</div>
            <div className="text-xs text-gray-500">Page ID: {pageId || '-'}</div>
          </div>
          <div className="flex items-center space-x-3">
            <a className="text-blue-600 text-sm underline" href={snapshotUrl} target="_blank" rel="noreferrer">Open Snapshot</a>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-700">
          <div><span className="text-gray-500">Start:</span> {start || '-'}</div>
          <div><span className="text-gray-500">Stop:</span> {stop || '-'}</div>
          <div><span className="text-gray-500">Platforms:</span> {platforms || '-'}</div>
        </div>
        <div className="mt-3">
          <div className="text-xs text-gray-500 mb-1">Inline preview</div>
          {pv.mediaUrl ? (
            <div className="border rounded p-2 bg-gray-50">
              {pv.mediaType === 'video' ? (
                <video src={pv.mediaUrl} controls playsInline className="max-h-56 rounded w-full" />
              ) : (
                <img src={pv.mediaUrl} alt="ad preview" className="max-h-56 rounded w-auto" />
              )}
            </div>
          ) : (
            <div className="border rounded bg-white">
              <iframe
                title={`ad-${id}`}
                src={`https://api.glowytics.com/api/meta-ads/library/preview/embed?brandId=${encodeURIComponent(brandId || '')}&url=${encodeURIComponent(snapshotUrl)}`}
                className="w-full"
                style={{ height: 360 }}
                loading="lazy"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const sampleCurl = useMemo(() => {
    return `curl -G \\\n  -d "search_terms='${searchTerms || 'example'}'" \\\n  -d "ad_type=${adType}" \\\n  -d "ad_reached_countries=${countries || "['TR']"}" \\\n  "https://graph.facebook.com/v18.0/ads_archive"`;
  }, [searchTerms, adType, countries]);

  const currentFirstPageId = useMemo(() => {
    const raw = (pageIds || '').trim();
    if (!raw) return '';
    const list = raw.split(',').map(s => s.trim()).filter(Boolean);
    return list[0] || '';
  }, [pageIds]);

  const buildFacebookAdLibraryUrl = (pid) => {
    if (!pid) return '';
    // Mirror the UI link pattern provided
    const params = new URLSearchParams({
      active_status: 'active',
      ad_type: 'all',
      country: 'TR',
      is_targeted_country: 'false',
      media_type: 'all',
      search_type: 'page',
      source: 'fb-logo',
      view_all_page_id: String(pid)
    });
    return `https://www.facebook.com/ads/library/?${params.toString()}`;
  };

  // Call our backend with the same semantics as the FB UI link:
  // active_status=active, ad_type=all, country=TR, media_type=all, search_type=page(view_all_page_id)
  const fetchViaLinkParams = useCallback(async () => {
    try {
      const pid = currentFirstPageId;
      if (!pid) {
        toast.error('Select a Page first');
        return;
      }
      setLoading(true);
      setRows([]);
      setAfter('');
      setPagingNext('');
      setPageIds(String(pid));
      const params = new URLSearchParams();
      params.set('ad_active_status', 'ACTIVE');
      params.set('ad_type', 'ALL');
      // Meta Ad Library API limitation: non-political ads are available via API only for UK/EU.
      // Use GB to retrieve ads for the selected page.
      params.set('ad_reached_countries', "['GB']");
      params.set('search_page_ids', JSON.stringify([String(pid)]));
      params.set('media_type', 'ALL');
      params.set('limit', String(limit || 50));
      const url = `https://api.glowytics.com/api/meta-ads/library/search?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include', headers: baseHeaders, method: 'GET' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Meta Ad Library request failed');
      const list = Array.isArray(data?.data) ? data.data : [];
      setRows(list);
      const next = data?.paging?.next || '';
      const cursorAfter = data?.paging?.cursors?.after || '';
      setPagingNext(next || '');
      setAfter(cursorAfter || '');
      toast.success(`Fetched ${list.length} ads (TR • active • all)`);
    } catch (e) {
      toast.error(e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [currentFirstPageId, limit, baseHeaders]);

  // Political-only TR fetch (per API availability in TR)
  const fetchPoliticalTR = useCallback(async () => {
    try {
      const pid = currentFirstPageId;
      if (!pid) {
        toast.error('Select a Page first');
        return;
      }
      setLoading(true);
      setRows([]);
      setAfter('');
      setPagingNext('');
      setPageIds(String(pid));
      const params = new URLSearchParams();
      params.set('ad_active_status', 'ACTIVE'); // or ALL
      params.set('ad_type', 'POLITICAL_AND_ISSUE_ADS');
      params.set('ad_reached_countries', "['TR']");
      params.set('search_page_ids', JSON.stringify([String(pid)]));
      params.set('media_type', 'ALL');
      params.set('limit', String(limit || 50));
      const url = `https://api.glowytics.com/api/meta-ads/library/search?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include', headers: baseHeaders, method: 'GET' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Meta Ad Library request failed');
      const list = Array.isArray(data?.data) ? data.data : [];
      setRows(list);
      const next = data?.paging?.next || '';
      const cursorAfter = data?.paging?.cursors?.after || '';
      setPagingNext(next || '');
      setAfter(cursorAfter || '');
      toast.success(`Fetched ${list.length} political ads (TR • active)`);
    } catch (e) {
      toast.error(e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [currentFirstPageId, limit, baseHeaders]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Meta Ad Library • Live Search</h1>
        <p className="text-gray-600 mt-1">
          Queries Meta's Ad Library API in real time via backend using your brand's Meta token.
        </p>
      </div>

      {/* Simplified controls */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <div className="lg:col-span-2">
          <label className="block text-sm text-gray-700 mb-1">Resolve by Instagram username</label>
          <div className="flex">
            <input value={uname} onChange={(e) => setUname(e.target.value)} className="flex-1 border rounded-l px-3 py-2" placeholder="@username" />
            <button onClick={resolveUsername} disabled={unameLoading} className="px-3 py-2 rounded-r bg-gray-800 text-white disabled:opacity-50">
              {unameLoading ? 'Adding…' : 'Add'}
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-1">Search uses the resolved advertiser Page ID. Active status = ALL, window = last 90 days.</div>
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Reached countries (JSON array or CSV)</label>
          <input value={countries} onChange={(e) => setCountries(e.target.value)} className="w-full border rounded px-3 py-2" placeholder="['TR'] or US,GB" />
        </div>
        <div className="flex items-end">
          <button
            onClick={runSearch}
            disabled={loading}
            className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </div>

      {/* New: Search Facebook Pages by keyword and list Page IDs */}
      <div className="mb-4 border rounded p-3 bg-white">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm text-gray-700 mb-1">Search Facebook pages by keyword</label>
            <input
              value={advQuery}
              onChange={(e) => setAdvQuery(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g., penti"
            />
          </div>
          <button
            onClick={findAdvertisers}
            disabled={advLoading}
            className="px-4 py-2 rounded bg-gray-800 text-white disabled:opacity-50"
          >
            {advLoading ? 'Searching…' : 'Find pages'}
          </button>
        </div>
        {advResults && advResults.length > 0 && (
          <div className="mt-3 overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Page name</th>
                  <th className="py-2 pr-4">Page ID</th>
                  <th className="py-2 pr-4">Username</th>
                  <th className="py-2 pr-4">Link</th>
                  <th className="py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {advResults.map((a) => (
                  <tr key={a.page_id} className="border-b">
                    <td className="py-2 pr-4">{a.page_name || '-'}</td>
                    <td className="py-2 pr-4">{a.page_id}</td>
                    <td className="py-2 pr-4">{a.username || '-'}</td>
                    <td className="py-2 pr-4">
                      {a.link ? (
                        <a href={a.link} target="_blank" rel="noreferrer" className="text-blue-600 underline">Open</a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      <button
                        onClick={() => searchForPageId(String(a.page_id))}
                        className="px-2 py-1 rounded bg-blue-600 text-white"
                      >
                        View ads
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Publisher platforms (CSV or JSON array)</label>
          <input value={publisherPlatforms} onChange={(e) => setPublisherPlatforms(e.target.value)} className="w-full border rounded px-3 py-2" placeholder="FACEBOOK,INSTAGRAM" />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Media type</label>
          <select value={mediaType} onChange={(e) => setMediaType(e.target.value)} className="w-full border rounded px-3 py-2">
            <option value="ALL">ALL</option>
            <option value="IMAGE">IMAGE</option>
            <option value="MEME">MEME</option>
            <option value="VIDEO">VIDEO</option>
            <option value="NONE">NONE</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Limit</label>
          <input type="number" min={1} max={1000} value={limit} onChange={(e) => setLimit(e.target.value)} className="w-full border rounded px-3 py-2" />
        </div>
        <div className="flex items-end">
          {after && (
            <button
              onClick={loadMore}
              disabled={loading}
              className="ml-3 px-4 py-2 rounded bg-gray-700 text-white disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <div className="text-xs text-gray-500">Example cURL (docs):</div>
        <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto">{sampleCurl}</pre>
        <div className="text-xs text-gray-500 mt-1">
          See Meta Ad Library API docs: <a className="underline text-blue-600" href="https://www.facebook.com/ads/library/api/" target="_blank" rel="noreferrer">Meta Ad Library API</a>
        </div>
      </div>

      {/* Open in Facebook Ad Library (for the first selected Page ID) */}
      <div className="mb-4">
        <button
          onClick={() => {
            const url = buildFacebookAdLibraryUrl(currentFirstPageId);
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          }}
          disabled={!currentFirstPageId}
          className="px-4 py-2 rounded bg-black text-white disabled:opacity-40"
          title={!currentFirstPageId ? 'Pick a Page (View ads) first' : 'Open on Facebook'}
        >
          Open on Facebook Ad Library (TR • active • all)
        </button>
        <button
          onClick={fetchViaLinkParams}
          disabled={!currentFirstPageId || loading}
          className="ml-3 px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-40"
          title={!currentFirstPageId ? 'Pick a Page first' : 'Fetch via API with the same params'}
        >
          Fetch via API (TR • active • all)
        </button>
        <button
          onClick={fetchPoliticalTR}
          disabled={!currentFirstPageId || loading}
          className="ml-3 px-4 py-2 rounded bg-rose-600 text-white disabled:opacity-40"
          title={!currentFirstPageId ? 'Pick a Page first' : 'Fetch political-only in TR'}
        >
          Fetch via API (TR • political-only)
        </button>
        {!currentFirstPageId && (
          <div className="text-xs text-gray-500 mt-1">
            Select a Page from the table (View ads) to enable this button.
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="text-sm text-gray-700 mb-2">Results: {rows.length}</div>
        {rows.map(renderRow)}
        {pagingNext && (
          <div className="mt-4">
            <button
              onClick={loadMore}
              disabled={loading}
              className="px-4 py-2 rounded bg-gray-700 text-white disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load next page'}
            </button>
          </div>
        )}
      </div>

      {/* Auto Snapshot of the selected Page ID (server-side headless screenshot) */}
      <div className="mt-8 border rounded p-3 bg-white">
        <div className="text-sm font-semibold mb-2">Auto Snapshot (server-side)</div>
        <AutoSnapshotBlock pid={currentFirstPageId} />
      </div>

      {/* Manual video embed test via proxy (mp4) */}
      <div className="mt-8 border rounded p-3 bg-white">
        <div className="text-sm font-semibold mb-2">Video embed (via proxy)</div>
        <VideoEmbedBlock />
      </div>

      {/* Crawl ad asset links */}
      <div className="mt-8 border rounded p-3 bg-white">
        <div className="text-sm font-semibold mb-2">Crawl ad asset links (experimental)</div>
        <CrawlerBlock />
      </div>

    </div>
  );
}

/* removed inline embed helpers */

function VideoEmbedBlock() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const [url, setUrl] = React.useState(
    'https://video.xx.fbcdn.net/o1/v/t2/f2/m69/AQNlfKsQISaFp2mhAGAVTStbTe6zsTXeM3TABq23t-DaOoNczLKXpZoZPbFpW68Cwd5OeZxJbctc-a24mFQc0uwq.mp4?strext=1&_nc_cat=104&_nc_oc=AdlfSGzSFeJARtF4cz2JyHeZZrjmgGAYN_aVEqZLSc14F0dBeGNqDYs63lw4SBpzEro&_nc_sid=8bf8fe&_nc_ht=video.fist6-2.fna.fbcdn.net&_nc_ohc=UvW84YEtb_sQ7kNvwHM2la2&efg=eyJ2ZW5jb2RlX3RhZyI6Inhwdl9wcm9ncmVzc2l2ZS5WSV9VU0VDQVNFX1BST0RVQ1RfVFlQRS4uQzMuMzYwLnN2ZV9zZCIsInhwdl9hc3NldF9pZCI6MTAwMzczOTA1ODM1MDE1NiwiYXNzZXRfYWdlX2RheXMiOjI4MiwidmlfdXNlY2FzZV9pZCI6MTA3OTksImR1cmF0aW9uX3MiOjMwLCJ1cmxnZW5fc291cmNlIjoid3d3In0%3D&ccb=17-1&_nc_gid=dW-6vbSoU9UNB_6xgoq2CQ&_nc_zt=28&oh=00_Afh9y05hrtuF2SZ-_0J2gf6Pdtqvpe985TfBB-mSowtYRQ&oe=69328756'
  );
  const proxied =
    url && brandId
      ? `https://api.glowytics.com/api/meta-ads/library/preview/media?brandId=${encodeURIComponent(
          brandId
        )}&url=${encodeURIComponent(url)}`
      : '';
  return (
    <div>
      <div className="flex gap-3">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="flex-1 border rounded px-3 py-2"
          placeholder="https://video.xx.fbcdn.net/...mp4"
        />
      </div>
      {proxied && (
        <div className="mt-3">
          <video src={proxied} controls playsInline className="w-full rounded" />
        </div>
      )}
    </div>
  );
}

function CrawlerBlock() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const [url, setUrl] = React.useState(
    'https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=TR&is_targeted_country=false&media_type=all&search_type=page&view_all_page_id=33709448234'
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [assets, setAssets] = React.useState([]);
  const crawl = async () => {
    try {
      if (!brandId) return;
      setIsLoading(true);
      setAssets([]);
      const u = new URLSearchParams({ url });
      const res = await fetch(`https://api.glowytics.com/api/meta-ads/library/crawl-links?${u.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'crawl_failed');
      setAssets(Array.isArray(data.assets) ? data.assets : []);
      toast.success(`Found ${Array.isArray(data.assets) ? data.assets.length : 0} assets`);
    } catch (e) {
      toast.error(e.message || 'Crawl failed');
    } finally {
      setIsLoading(false);
    }
  };
  const proxify = (u) =>
    brandId ? `https://api.glowytics.com/api/meta-ads/library/preview/media?brandId=${encodeURIComponent(brandId)}&url=${encodeURIComponent(u)}` : u;
  return (
    <div>
      <div className="flex gap-3">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="flex-1 border rounded px-3 py-2"
          placeholder="Ad Library brand page URL"
        />
        <button onClick={crawl} disabled={isLoading} className="px-3 py-2 rounded bg-gray-800 text-white disabled:opacity-50">
          {isLoading ? 'Crawling…' : 'Crawl'}
        </button>
      </div>
      {assets && assets.length > 0 && (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {assets.slice(0, 24).map((a, i) => (
            <div key={`${a.url}-${i}`} className="border rounded p-2">
              <div className="text-xs text-gray-500 mb-1">
                {a.type} • {a.source}
              </div>
              {a.type === 'video' ? (
                <video src={proxify(a.url)} controls playsInline className="w-full rounded" />
              ) : (
                <img src={proxify(a.url)} alt="asset" className="w-full rounded" />
              )}
              <div className="mt-1 text-xs break-all">
                <a href={a.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  open source
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AutoSnapshotBlock({ pid }) {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const [ts, setTs] = React.useState(Date.now());
  const [imgErr, setImgErr] = React.useState(false);
  React.useEffect(() => { setTs(Date.now()); }, [pid]);
  const src = pid
    ? `https://api.glowytics.com/api/meta-ads/library/snapshot?brandId=${encodeURIComponent(
        brandId || ''
      )}&view_all_page_id=${encodeURIComponent(pid)}&country=TR&active_status=active&ad_type=all&media_type=all&_=${ts}`
    : '';
  const fbUrl = pid
    ? `https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=TR&is_targeted_country=false&media_type=all&search_type=page&view_all_page_id=${encodeURIComponent(
        pid
      )}`
    : '';
  return (
    <div>
      <div className="text-xs text-gray-500 mb-2">
        Shows a server-side screenshot of the Facebook Ad Library page for the selected Page ID.
      </div>
      {!pid ? (
        <div className="text-xs text-gray-400">Select a Page (View ads) first to load snapshot.</div>
      ) : imgErr ? (
        <div className="p-3 rounded border bg-yellow-50 text-sm text-gray-700">
          Could not capture snapshot on the server. This can happen if the headless browser is not available.
          <div className="mt-2">
            <a href={fbUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
              Open on Facebook Ad Library
            </a>
          </div>
        </div>
      ) : (
        <img
          src={src}
          alt="Ad Library snapshot"
          className="w-full rounded border"
          onError={() => setImgErr(true)}
        />
      )}
    </div>
  );
}

/* removed inline embed helpers */


